import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $connectionStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { IntegrationStep, type IntegrationResponse } from '@shared/api';
import { Button, Spinner, Text } from '@shared/widgets';
import xeroImg from '@assets/icons/integrations/xero-logo.svg?url';

interface IntegrationCardProps {
  integration: IntegrationResponse;
}

export const IntegrationCard = (props: IntegrationCardProps) => {
  const { navigate } = useRouteHook();

  const connectionStore = useUnit($connectionStore.store);

  const { t } = useTranslation('integrations');

  return (
    <div
      className={cn(
        'bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
      )}
    >
      <div className='flex'>
        <div className='mr-5 flex h-24 w-24 flex-none items-center justify-center bg-ice text-center dark:bg-eerie-black'>
          <img className='flex-none object-contain' src={xeroImg} alt='Xero' />
        </div>
        <div>
          <Text className='mb-1' weight='medium' size='xl'>
            {props.integration.name}
          </Text>
          <Text size='sm' color='secondary'>
            {t(`card.description.${props.integration.name}`)}
          </Text>
        </div>
      </div>
      <div className='-mr-5 mt-5 flex items-center'>
        {props.integration.connected ? (
          <>
            <Button
              className='flex-grow-1 mr-5'
              color='outlined'
              fullWidth
              onClick={() => navigate(RoutePaths.integration.onNavigate(props.integration.name))}
            >
              {t('card.buttons.configure')}
            </Button>
            <Button
              className='flex-grow-1 mr-5'
              color='outlined'
              fullWidth
              onClick={() => $connectionStore.events.disconnect(props.integration.name)}
              disabled={connectionStore.loading || connectionStore.data === IntegrationStep.Disconnect}
            >
              {t('card.buttons.disconnect')}
              {(connectionStore.loading || connectionStore.data === IntegrationStep.Disconnect) && <Spinner embedded />}
            </Button>
          </>
        ) : (
          <Button
            className='flex-grow-1 mr-5'
            color='primary'
            fullWidth
            onClick={() => $connectionStore.events.connect(props.integration.name)}
            disabled={connectionStore.loading || connectionStore.data === IntegrationStep.Connect}
          >
            {t('card.buttons.integrate')}
            {(connectionStore.loading || connectionStore.data === IntegrationStep.Connect) && <Spinner embedded />}
          </Button>
        )}
      </div>
    </div>
  );
};
